import { useSelector } from 'react-redux';
import {
  formValueSelector,
  getFormMeta,
  getFormSyncErrors,
  getFormValues,
  hasSubmitFailed,
} from 'redux-form';

import { RootState } from 'app/store/rootReducer';
import { FormsEnum } from 'app/shared/enums';
import { useFormConfig } from 'app/shared/hooks/useFormConfig';
import { QuotationService } from 'app/services/quotation.service';
import { registeredFieldsSelector } from 'app/store/data/form';

interface FormValues {}
interface FormValuesByModelPath {}

export function useForm<
  T extends FormValues = {},
  M extends FormValuesByModelPath = {}
>(formName: FormsEnum) {
  const { questions } = useFormConfig(formName);

  const formValues = useSelector(
    (state: RootState) => getFormValues(formName)(state) as T
  );

  const formValuesByModelPath =
    questions &&
    QuotationService.getValuesByModelPaths<M>(formValues, questions);

  const formSubmitFailed = useSelector((state: RootState) =>
    hasSubmitFailed(formName)(state)
  );

  const formSyncErrors = useSelector((state: RootState) =>
    getFormSyncErrors(formName)(state)
  );

  const formMeta = useSelector((state: RootState) =>
    getFormMeta(formName)(state)
  );

  const registeredFields = useSelector(registeredFieldsSelector(formName));

  const registeredFieldNames = registeredFields
    ? Object.values(registeredFields).map(({ name }) => name)
    : [];

  const valuesForRegisteredFieldsOnly = useSelector((state: RootState) =>
    registeredFieldNames.length
      ? formValueSelector(formName)(state, ...registeredFieldNames)
      : {}
  );

  return {
    formMeta,
    formSubmitFailed,
    formSyncErrors,
    formValues,
    formValuesByModelPath,
    valuesForRegisteredFieldsOnly,
  };
}
